import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Row, Col } from 'reactstrap';
import { toast } from 'react-toastify';
import ToolkitProvider from 'react-bootstrap-table2-toolkit';
import BootstrapTable from 'react-bootstrap-table-next';

import DefaultModal from 'components/Modal/DefaultModal';
import Pagination from 'components/Pagination';
import ReactButton from 'components/Buttons/ReactButton';
import ReactLoader from 'components/Loader';
import {
  individual_column_style,
  individual_header_style
} from '../../../../../assets/styles/table_custom_style';
import { PhoneCell } from 'components/FlexTable/components/cells/phone';
import BroadcastListService from '../../../service';

const PAGE_SIZE = 10;

const recordTypeConfig = {
  lead: {
    title: 'Leads da Campanha',
    idField: 'lead_id',
    idColumnText: 'Lead ID',
    errorMessage: 'Erro ao carregar leads da campanha'
  },
  opportunity: {
    title: 'Oportunidades da Campanha',
    idField: 'opportunity_id',
    idColumnText: 'Oportunidade ID',
    errorMessage: 'Erro ao carregar oportunidades da campanha'
  }
};

const BroadcastListRecordsModal = ({
  isModalOpen,
  onModalToggle,
  broadcastListId,
  recordType,
  ...props
}) => {
  const [activePage, setActivePage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [allResults, setAllResults] = useState([]);

  const config = recordTypeConfig[recordType] || recordTypeConfig.lead;

  const filteredResults = useMemo(() => {
    return allResults.filter((row) => row[config.idField]);
  }, [allResults, config.idField]);

  const paginatedResults = useMemo(() => {
    const start = (activePage - 1) * PAGE_SIZE;
    return filteredResults.slice(start, start + PAGE_SIZE);
  }, [filteredResults, activePage]);

  const handleGetResults = useCallback(() => {
    if (!isModalOpen || !broadcastListId) return;
    setLoading(true);

    BroadcastListService.getResults(broadcastListId, {
      page: 1,
      size: 9999
    })
      .then((response) => {
        const data = response.data?.data?.result || [];
        setAllResults(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        toast.error(config.errorMessage);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [isModalOpen, broadcastListId, config.errorMessage]);

  useEffect(() => {
    if (isModalOpen) {
      setActivePage(1);
      handleGetResults();
    }
  }, [isModalOpen, handleGetResults]);

  const handlePageChange = (page) => {
    setActivePage(page);
  };

  return (
    <DefaultModal
      show={isModalOpen}
      onModalToggle={onModalToggle}
      title={config.title}
      showCloseButton={false}
      modalContainerClasses="modal-lg"
      {...props}
    >
      <Row>
        <Col>
          <ToolkitProvider
            data={paginatedResults}
            keyField="id"
            columns={[
              {
                dataField: 'cliente_nome',
                text: 'Titulo',
                sort: true,
                style: individual_column_style,
                headerStyle: individual_header_style
              },
              {
                dataField: 'cliente_telefone',
                text: 'Telefone',
                sort: true,
                style: individual_column_style,
                headerStyle: individual_header_style,
                formatter: (_, row) => (
                  <PhoneCell
                    phone={row.cliente_telefone}
                    ddi={row.cliente_ddi}
                  />
                )
              },
              {
                dataField: config.idField,
                text: config.idColumnText,
                sort: true,
                style: individual_column_style,
                headerStyle: individual_header_style,
                formatter: (_, row) => row[config.idField] || '-'
              }
            ]}
          >
            {(tableProps) => (
              <div className="py-4 table-responsive">
                {loading ? (
                  <Row className="justify-content-md-center">
                    <ReactLoader />
                  </Row>
                ) : (
                  <BootstrapTable
                    {...tableProps.baseProps}
                    bootstrap4={true}
                    bordered={false}
                    classes="align-items-center"
                  />
                )}
              </div>
            )}
          </ToolkitProvider>
          {filteredResults.length > PAGE_SIZE && (
            <Pagination
              activePage={activePage}
              totalItemsCount={filteredResults.length}
              onChange={handlePageChange}
            />
          )}
        </Col>
      </Row>
      <Row>
        <Col style={{ maxWidth: '8rem' }}>
          <ReactButton
            btnColor="confirmation"
            type="submit"
            size="sm"
            onClick={() => onModalToggle(false)}
            block
          >
            OK
          </ReactButton>
        </Col>
      </Row>
    </DefaultModal>
  );
};

export default BroadcastListRecordsModal;
